import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, Loader2, Lock, Menu, Save, User as UserIcon, CheckCircle, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import { authApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { getAllUiLanguages, sortUiLanguagesForPicker } from '@/data/locales';
import { setAppLanguage } from '@/i18n';

export default function AccountPage() {
  const { t } = useTranslation();
  const { user, role, company, syncSession } = useAuthStore();
  const { toggleSidebar } = useUIStore();
  const qc = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['myProfile'],
    queryFn: async () => {
      const res = await authApi.getMyProfile();
      return res.data.message as {
        full_name: string;
        email: string;
        role: string;
        ui_language: string;
        company_default_ui_language: string;
        company_name?: string;
      };
    },
  });

  const { data: companyCountry } = useQuery({
    queryKey: ['companyProfile'],
    queryFn: async () => {
      const res = await authApi.getCompanyProfile();
      return (res.data.message?.country as string) || 'GB';
    },
    staleTime: 300_000,
  });

  const [uiLanguage, setUiLanguage] = useState('en');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (profile?.ui_language) setUiLanguage(profile.ui_language);
  }, [profile]);

  useEffect(() => {
    if (profile?.ui_language) {
      void setAppLanguage(profile.ui_language, profile.company_default_ui_language);
    }
  }, [profile?.ui_language, profile?.company_default_ui_language]);

  const languageOptions = useMemo(() => {
    const withLabels = getAllUiLanguages().map((l) => ({
      ...l,
      label: t(`languages.${l.code}`, { defaultValue: l.label }),
    }));
    return sortUiLanguagesForPicker(withLabels, {
      selectedCode: uiLanguage,
      countryCode: companyCountry,
    });
  }, [uiLanguage, companyCountry, t]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (newPassword && newPassword !== confirmPassword) {
        throw new Error(t('account.passwordMismatch'));
      }
      if (newPassword && newPassword.length < 6) {
        throw new Error(t('account.passwordTooShort'));
      }
      const res = await authApi.updateMyProfile({
        ui_language: uiLanguage,
        ...(newPassword ? { current_password: currentPassword, new_password: newPassword } : {}),
      });
      return res.data.message as { ui_language?: string };
    },
    onSuccess: (data) => {
      setMessage(t('account.saved'));
      setError('');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      const lang = data?.ui_language || uiLanguage;
      setAppLanguage(lang);
      if (user && company) {
        syncSession({
          user: { ...user, ui_language: lang },
          company,
          role,
          can_use_pos: useAuthStore.getState().canUsePOS,
          permissions: useAuthStore.getState().permissions,
        });
      }
      void qc.invalidateQueries({ queryKey: ['myProfile'] });
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        t('account.saveFailed');
      setError(msg);
      setMessage('');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setError('');
    saveMutation.mutate();
  };

  const displayName = profile?.full_name || user?.full_name || 'User';
  const displayEmail = profile?.email || user?.email || '';
  const displayRole = profile?.role || role || '';

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <TrialBanner />
        <div className="p-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-2 -ml-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg shrink-0"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center">
                <UserIcon className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('account.title')}</h1>
                <p className="text-gray-500 text-sm">{t('account.subtitle')}</p>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="card p-8 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="card p-6 space-y-8">
              <section className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <UserIcon className="w-5 h-5 text-primary-600" /> {t('account.profileSection')}
                </h2>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-primary-700 font-bold text-xl">{displayName[0]?.toUpperCase() || 'U'}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{displayName}</p>
                    <p className="text-sm text-gray-500">{displayEmail}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {displayRole}
                      {profile?.company_name ? ` · ${profile.company_name}` : ''}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('account.fullName')}</label>
                    <input className="input-field bg-gray-50" value={displayName} readOnly />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('account.email')}</label>
                    <input type="email" className="input-field bg-gray-50" value={displayEmail} readOnly />
                  </div>
                </div>
              </section>

              <section id="language" className="space-y-4 border-t border-gray-100 pt-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-primary-600" /> {t('account.languageSection')}
                </h2>
                <p className="text-sm text-gray-500">
                  {t('account.languageHelp', { lang: profile?.company_default_ui_language || 'en' })}
                </p>
                <select
                  className="input-field max-w-md"
                  value={uiLanguage}
                  onChange={(e) => setUiLanguage(e.target.value)}
                >
                  {languageOptions.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </section>

              <section id="password" className="space-y-4 border-t border-gray-100 pt-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-primary-600" /> {t('account.passwordSection')}
                </h2>
                <p className="text-sm text-gray-500">{t('account.passwordHelp')}</p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('account.currentPassword')}</label>
                  <input
                    type="password"
                    className="input-field max-w-md"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('account.newPassword')}</label>
                    <input
                      type="password"
                      className="input-field"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('account.confirmPassword')}</label>
                    <input
                      type="password"
                      className="input-field"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                </div>
              </section>

              {error && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" /> {error}
                </p>
              )}
              {message && (
                <p className="text-sm text-emerald-700 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" /> {message}
                </p>
              )}

              <button type="submit" disabled={saveMutation.isPending} className="btn-primary inline-flex items-center gap-2">
                {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t('account.saveChanges')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
